import { Link, useNavigate } from "react-router-dom";
import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  ArrowRight,
  ShieldCheck,
  Layers,
  BedDouble,
  KeyRound,
  ClipboardList,
  Receipt,
  Smartphone,
  CheckCircle2,
} from "lucide-react";

const HERO_IMAGE =
  "https://customer-assets.emergentagent.com/job_mission-visitor-flow/artifacts/3pnftvxi_Screenshot%202026-05-02%20at%2021.26.52.png";

const CONTACT_EMAIL = "kapsowar.hospitality@gmail.com";
const MAILTO = `mailto:${CONTACT_EMAIL}`;
const API_BASE = process.env.REACT_APP_BACKEND_URL;

const FEATURES = [
  {
    icon: Layers,
    title: "Visitor approval workflow",
    body: "Every visiting clinician moves through Department Head → CMO → Hospitality, with editable email templates, push notifications, and a full audit trail attached to each request.",
  },
  {
    icon: BedDouble,
    title: "Hotel-style guest housing",
    body: "Multi-segment stays across guest houses, cottages and shared rooms. Rate cards, live availability calendar, conflict detection, room sharing overrides, and PDF invoices.",
  },
  {
    icon: KeyRound,
    title: "Long-term leases & rentals",
    body: "Manage staff and long-term residents on the same units. Monthly billing, deposits, recurring add-ons for utilities, trash, security and house helpers — open-ended or fixed-term.",
  },
  {
    icon: ClipboardList,
    title: "Daily housekeeping & repairs",
    body: "Auto-generated worksheets list every check-out, check-in, turnover and open repair. Email it to your housekeeping team or download a printable PDF in one click.",
  },
  {
    icon: Receipt,
    title: "Invoices, rates & audit log",
    body: "Per-stay or per-month invoices, regenerable when bookings change, with a stale-invoice nudge so coordinators always bill the right amount. Every action is logged and exportable.",
  },
  {
    icon: Smartphone,
    title: "Mobile apps + multi-tenant",
    body: "Native iOS and Android apps published under your hospital. Each organisation lives in its own isolated tenant — users, visitors, leases, invoices and housekeeping never cross.",
  },
];

const WORKFLOW_STEPS = [
  {
    step: "01",
    title: "Approve & schedule",
    body: "Visitor requests flow through Department Head → CMO → Hospitality with auto-emails, push alerts and a complete audit trail. Coordinators can act on behalf of an absent CMO when needed.",
  },
  {
    step: "02",
    title: "Place in housing",
    body: "Approved visitors get an auto-drafted booking the coordinator just confirms. Long-term residents go on a lease. Both share the same calendar and rate cards — no double bookings, ever.",
  },
  {
    step: "03",
    title: "Run the day",
    body: "Each morning a housekeeping worksheet is generated with turnovers, check-ins and open repairs. Email it to cleaners, log work orders and track progress to completion.",
  },
  {
    step: "04",
    title: "Bill & reconcile",
    body: "Stay invoices for visitors, monthly invoices for tenants — generated from one source of truth. Edit a booking and the invoice can be refreshed in one click; everything is exportable.",
  },
];

const AUDIENCE = [
  "Mission & faith-based hospitals hosting visiting clinicians",
  "Hospitals operating staff housing, guest houses or rental cottages",
  "Compounds coordinating cleaners, security and house-helper rosters",
  "Teaching hospitals running structured electives & fellowships",
  "Sending agencies coordinating short-term medical teams",
];

export default function LandingPage() {
  const { user, loading } = useAuth();
  const navigate = useNavigate();

  const [form, setForm] = useState({
    hospital_name: "",
    contact_name: "",
    contact_email: "",
    country: "",
    expected_users: "",
    notes: "",
    website: "", // honeypot
  });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const updateField = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (submitting) return;
    if (!form.hospital_name.trim() || !form.contact_name.trim() || !form.contact_email.trim()) {
      toast.error("Please fill in hospital, contact name and email");
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API_BASE}/api/public/signup-request`, form, {
        headers: { "Content-Type": "application/json" },
      });
      setSubmitted(true);
      toast.success("Thanks — we'll be in touch within one working day.");
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Something went wrong. Please email us directly.");
    } finally {
      setSubmitting(false);
    }
  };

  useEffect(() => {
    if (user && !loading) {
      const dest = user.role === "global_admin" ? "/organizations" : "/dashboard";
      navigate(dest, { replace: true });
    }
  }, [user, loading, navigate]);

  return (
    <div className="min-h-screen bg-[#FCFCFB] text-[#1C1F1E]" data-testid="landing-page">
      {/* Nav */}
      <header
        className="sticky top-0 z-40 backdrop-blur bg-[#FCFCFB]/85 border-b border-[#E8E8E6]"
        data-testid="landing-nav"
      >
        <div className="max-w-6xl mx-auto px-5 md:px-10 h-16 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-md bg-[#1B4332] flex items-center justify-center">
              <CheckCircle2 className="h-4 w-4 text-[#E9F5DB]" />
            </div>
            <span
              className="font-semibold tracking-tight text-[#1B4332]"
              style={{ fontFamily: "Manrope, sans-serif" }}
            >
              Mission Hospitality
            </span>
          </div>
          <div className="flex items-center gap-2 md:gap-3">
            <Link
              to="/visit"
              className="hidden sm:inline text-sm font-medium text-[#525C58] hover:text-[#1B4332] transition-colors"
              data-testid="landing-nav-visit"
            >
              Planning a visit?
            </Link>
            <a
              href="#request-account"
              className="hidden sm:inline text-sm font-medium text-[#525C58] hover:text-[#1B4332] transition-colors"
              data-testid="landing-nav-contact"
            >
              Contact
            </a>
            <Link to="/login" data-testid="landing-nav-signin">
              <Button
                variant="outline"
                className="h-9 px-4 border-[#1B4332]/30 text-[#1B4332] hover:bg-[#1B4332] hover:text-white transition-colors"
              >
                Sign in
              </Button>
            </Link>
          </div>
        </div>
      </header>

      {/* Hero */}
      <section className="relative overflow-hidden" data-testid="landing-hero">
        <div className="max-w-6xl mx-auto px-5 md:px-10 pt-24 md:pt-36 pb-16 md:pb-24 grid md:grid-cols-12 gap-10 md:gap-14 items-center">
          <div className="md:col-span-6 space-y-6">
            <span
              className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-[#E9F5DB] text-[#1B4332] text-xs font-medium"
              data-testid="landing-hero-eyebrow"
            >
              <span className="w-1.5 h-1.5 rounded-full bg-[#2D6A4F]" />
              Purpose-built for mission hospitals · Kenya
            </span>
            <h1
              className="text-4xl sm:text-5xl lg:text-6xl font-semibold tracking-tight text-[#1C1F1E]"
              style={{ fontFamily: "Manrope, sans-serif" }}
              data-testid="landing-hero-title"
            >
              Host visitors, manage housing,{" "}
              <span className="text-[#1B4332]">run the day.</span>
            </h1>
            <p className="text-base md:text-lg text-[#525C58] leading-relaxed max-w-xl">
              A quiet, dependable platform for mission hospitals — visitor approvals,
              hotel-style guest housing, long-term leases, daily housekeeping worksheets
              and invoicing all in one place. Built for hospitals juggling specialists,
              guest houses, residents and house helpers without losing the thread.
            </p>
            <div className="flex flex-col sm:flex-row gap-3 pt-2">
              <a href="#request-account" data-testid="landing-hero-cta">
                <Button className="h-12 px-6 bg-[#1B4332] hover:bg-[#2D6A4F] text-white text-base font-medium transition-colors">
                  Request your hospital's account
                  <ArrowRight className="h-4 w-4 ml-2" />
                </Button>
              </a>
              <a
                href="#how-it-works"
                className="inline-flex items-center justify-center h-12 px-5 text-[#1B4332] font-medium hover:bg-[#E9F5DB]/60 rounded-md transition-colors"
                data-testid="landing-hero-secondary"
              >
                See how it works
              </a>
            </div>
            <p className="text-xs text-[#7A8A84] pt-1">
              Free to evaluate. No credit card. Write to us and we'll provision your
              hospital within a working day.
            </p>
          </div>

          <div className="md:col-span-6 relative" data-testid="landing-hero-image-wrap">
            <div className="absolute -inset-4 bg-[#1B4332]/8 rounded-3xl rotate-1" />
            <div className="relative rounded-2xl overflow-hidden shadow-2xl border border-[#E8E8E6]">
              <img
                src={HERO_IMAGE}
                alt="A Land Rover on a rural mission road in Kenya"
                className="w-full h-[360px] md:h-[480px] object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/55 via-transparent to-transparent" />
              <div className="absolute bottom-5 left-5 right-5 text-white">
                <p className="text-xs uppercase tracking-widest opacity-80">
                  From the field
                </p>
                <p
                  className="text-lg md:text-xl font-medium leading-snug"
                  style={{ fontFamily: "Manrope, sans-serif" }}
                >
                  Hospitality, housing and housekeeping — one quiet platform for the whole compound.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Stats strip */}
      <section className="border-y border-[#E8E8E6] bg-white" data-testid="landing-stats">
        <div className="max-w-6xl mx-auto px-5 md:px-10 py-8 grid grid-cols-2 md:grid-cols-4 gap-6 md:gap-10">
          {[
            { k: "5+", v: "Workflows in one platform" },
            { k: "2-tier", v: "Housing — short stays + leases" },
            { k: "iOS + Android", v: "Native mobile apps" },
            { k: "100%", v: "Per-hospital data isolation" },
          ].map((s) => (
            <div key={s.v} className="space-y-1">
              <div
                className="text-2xl md:text-3xl font-semibold text-[#1B4332]"
                style={{ fontFamily: "Manrope, sans-serif" }}
              >
                {s.k}
              </div>
              <div className="text-sm text-[#525C58]">{s.v}</div>
            </div>
          ))}
        </div>
      </section>

      {/* Features */}
      <section
        id="features"
        className="max-w-6xl mx-auto px-5 md:px-10 py-20 md:py-28"
        data-testid="landing-features"
      >
        <div className="max-w-2xl mb-12 md:mb-16">
          <p className="text-sm font-medium text-[#2D6A4F] uppercase tracking-wider mb-3">
            What you get
          </p>
          <h2
            className="text-3xl md:text-4xl font-semibold tracking-tight"
            style={{ fontFamily: "Manrope, sans-serif" }}
          >
            Everything a busy mission hospital needs — approvals, housing, housekeeping, billing.
          </h2>
        </div>

        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6">
          {FEATURES.map((f) => {
            const Icon = f.icon;
            return (
              <div
                key={f.title}
                className="group p-6 md:p-7 rounded-2xl border border-[#E8E8E6] bg-white hover:border-[#1B4332]/30 hover:shadow-md transition-all duration-200"
                data-testid={`landing-feature-${f.title.toLowerCase().replace(/[^a-z]+/g, "-")}`}
              >
                <div className="w-11 h-11 rounded-xl bg-[#E9F5DB] flex items-center justify-center mb-5 group-hover:bg-[#1B4332] transition-colors">
                  <Icon className="h-5 w-5 text-[#1B4332] group-hover:text-[#E9F5DB] transition-colors" />
                </div>
                <h3
                  className="text-lg font-semibold text-[#1C1F1E] mb-2"
                  style={{ fontFamily: "Manrope, sans-serif" }}
                >
                  {f.title}
                </h3>
                <p className="text-sm text-[#525C58] leading-relaxed">{f.body}</p>
              </div>
            );
          })}
        </div>
      </section>

      {/* How it works */}
      <section
        id="how-it-works"
        className="bg-[#1B4332] text-white"
        data-testid="landing-how"
      >
        <div className="max-w-6xl mx-auto px-5 md:px-10 py-20 md:py-28">
          <div className="max-w-2xl mb-12 md:mb-16">
            <p className="text-sm font-medium text-[#95D5B2] uppercase tracking-wider mb-3">
              How it works
            </p>
            <h2
              className="text-3xl md:text-4xl font-semibold tracking-tight"
              style={{ fontFamily: "Manrope, sans-serif" }}
            >
              Four lanes of work, one tidy system.
            </h2>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-5 md:gap-6">
            {WORKFLOW_STEPS.map((s) => (
              <div
                key={s.step}
                className="p-6 rounded-2xl bg-white/5 border border-white/10 backdrop-blur hover:bg-white/10 transition-colors"
                data-testid={`landing-step-${s.step}`}
              >
                <div
                  className="text-sm font-mono text-[#95D5B2] mb-4"
                  style={{ fontFamily: "monospace" }}
                >
                  {s.step}
                </div>
                <h3
                  className="text-lg font-semibold mb-2"
                  style={{ fontFamily: "Manrope, sans-serif" }}
                >
                  {s.title}
                </h3>
                <p className="text-sm text-white/75 leading-relaxed">{s.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Audience */}
      <section
        className="max-w-6xl mx-auto px-5 md:px-10 py-20 md:py-28"
        data-testid="landing-audience"
      >
        <div className="grid md:grid-cols-12 gap-10 md:gap-16 items-center">
          <div className="md:col-span-5 space-y-5">
            <p className="text-sm font-medium text-[#2D6A4F] uppercase tracking-wider">
              Who it's for
            </p>
            <h2
              className="text-3xl md:text-4xl font-semibold tracking-tight"
              style={{ fontFamily: "Manrope, sans-serif" }}
            >
              Designed with mission hospitals, for mission hospitals.
            </h2>
            <p className="text-[#525C58] leading-relaxed">
              This platform was born inside a working mission hospital after years of
              juggling WhatsApp threads, paper booking calendars, lost emails and
              duplicated invoices. It's now available to hospitals facing the same
              hospitality, housing and housekeeping challenges.
            </p>
          </div>
          <div className="md:col-span-7">
            <ul className="space-y-3">
              {AUDIENCE.map((a) => (
                <li
                  key={a}
                  className="flex items-start gap-3 p-4 rounded-xl border border-[#E8E8E6] bg-white"
                  data-testid="landing-audience-item"
                >
                  <CheckCircle2 className="h-5 w-5 text-[#2D6A4F] shrink-0 mt-0.5" />
                  <span className="text-[#1C1F1E]">{a}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      {/* Request Account form */}
      <section
        id="request-account"
        className="relative overflow-hidden scroll-mt-20"
        data-testid="landing-cta"
      >
        <div className="max-w-6xl mx-auto px-5 md:px-10 pb-24">
          <div className="relative rounded-3xl overflow-hidden">
            <img
              src={HERO_IMAGE}
              alt=""
              className="absolute inset-0 w-full h-full object-cover"
            />
            <div className="absolute inset-0 bg-[#1B4332]/88" />
            <div className="relative p-6 md:p-12 lg:p-14 grid lg:grid-cols-12 gap-10 lg:gap-14 items-start">
              <div className="lg:col-span-5 text-white">
                <p className="text-sm font-medium text-[#95D5B2] uppercase tracking-wider mb-3">
                  Request your account
                </p>
                <h2
                  className="text-3xl md:text-4xl font-semibold tracking-tight mb-4"
                  style={{ fontFamily: "Manrope, sans-serif" }}
                >
                  Ready to onboard your hospital?
                </h2>
                <p className="text-white/85 text-base md:text-lg leading-relaxed mb-6">
                  Tell us a bit about your hospital. We'll provision your organisation,
                  seed your first admin account, set up your housing units and rate cards,
                  and walk your team through the first approval — usually within one
                  working day.
                </p>
                <div className="space-y-2 text-sm text-white/80">
                  <p className="flex items-center gap-2">
                    <CheckCircle2 className="h-4 w-4 text-[#95D5B2]" /> No credit card required
                  </p>
                  <p className="flex items-center gap-2">
                    <CheckCircle2 className="h-4 w-4 text-[#95D5B2]" /> Free to evaluate
                  </p>
                  <p className="flex items-center gap-2">
                    <CheckCircle2 className="h-4 w-4 text-[#95D5B2]" /> Your data, isolated from other hospitals
                  </p>
                </div>
                <p className="text-xs text-white/60 mt-8">
                  Prefer email? Write to{" "}
                  <a
                    href={MAILTO}
                    className="underline hover:text-white"
                    data-testid="landing-cta-email-link"
                  >
                    {CONTACT_EMAIL}
                  </a>
                </p>
              </div>

              <div className="lg:col-span-7">
                {submitted ? (
                  <div
                    className="bg-white rounded-2xl p-8 md:p-10 border border-white/20 shadow-xl text-center"
                    data-testid="landing-form-success"
                  >
                    <div className="w-14 h-14 rounded-full bg-[#E9F5DB] flex items-center justify-center mx-auto mb-5">
                      <CheckCircle2 className="h-7 w-7 text-[#1B4332]" />
                    </div>
                    <h3
                      className="text-2xl font-semibold text-[#1C1F1E] mb-2"
                      style={{ fontFamily: "Manrope, sans-serif" }}
                    >
                      Request received
                    </h3>
                    <p className="text-[#525C58] leading-relaxed max-w-sm mx-auto">
                      Thanks, {form.contact_name.split(" ")[0] || "there"} — we've
                      received your request for <strong>{form.hospital_name}</strong>.
                      Our team will reach out to{" "}
                      <strong>{form.contact_email}</strong> within one working day.
                    </p>
                    <Link to="/login" data-testid="landing-form-signin">
                      <Button
                        variant="outline"
                        className="mt-8 h-11 px-6 border-[#1B4332]/30 text-[#1B4332] hover:bg-[#1B4332] hover:text-white transition-colors"
                      >
                        I already have an account
                      </Button>
                    </Link>
                  </div>
                ) : (
                  <form
                    onSubmit={handleSubmit}
                    className="bg-white rounded-2xl p-6 md:p-8 border border-white/20 shadow-xl space-y-5"
                    data-testid="landing-signup-form"
                  >
                    {/* honeypot, hidden from real users */}
                    <input
                      type="text"
                      name="website"
                      value={form.website}
                      onChange={updateField("website")}
                      autoComplete="off"
                      tabIndex={-1}
                      aria-hidden="true"
                      style={{
                        position: "absolute",
                        left: "-10000px",
                        top: "auto",
                        width: "1px",
                        height: "1px",
                        overflow: "hidden",
                      }}
                    />

                    <div className="grid md:grid-cols-2 gap-5">
                      <div className="space-y-2">
                        <Label htmlFor="hospital_name" className="text-[#1C1F1E]">
                          Hospital name <span className="text-red-500">*</span>
                        </Label>
                        <Input
                          id="hospital_name"
                          value={form.hospital_name}
                          onChange={updateField("hospital_name")}
                          placeholder="e.g. Tenwek Hospital"
                          required
                          className="h-11"
                          data-testid="landing-form-hospital-name"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="country" className="text-[#1C1F1E]">
                          Country / location
                        </Label>
                        <Input
                          id="country"
                          value={form.country}
                          onChange={updateField("country")}
                          placeholder="Kenya"
                          className="h-11"
                          data-testid="landing-form-country"
                        />
                      </div>
                    </div>

                    <div className="grid md:grid-cols-2 gap-5">
                      <div className="space-y-2">
                        <Label htmlFor="contact_name" className="text-[#1C1F1E]">
                          Primary contact name <span className="text-red-500">*</span>
                        </Label>
                        <Input
                          id="contact_name"
                          value={form.contact_name}
                          onChange={updateField("contact_name")}
                          placeholder="Dr. Jane Doe"
                          required
                          className="h-11"
                          data-testid="landing-form-contact-name"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="contact_email" className="text-[#1C1F1E]">
                          Work email <span className="text-red-500">*</span>
                        </Label>
                        <Input
                          id="contact_email"
                          type="email"
                          value={form.contact_email}
                          onChange={updateField("contact_email")}
                          placeholder="jane@hospital.org"
                          required
                          className="h-11"
                          data-testid="landing-form-contact-email"
                        />
                      </div>
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="expected_users" className="text-[#1C1F1E]">
                        Expected number of users
                      </Label>
                      <Input
                        id="expected_users"
                        value={form.expected_users}
                        onChange={updateField("expected_users")}
                        placeholder="e.g. 10–20"
                        className="h-11"
                        data-testid="landing-form-expected-users"
                      />
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="notes" className="text-[#1C1F1E]">
                        Anything else you'd like us to know?
                      </Label>
                      <Textarea
                        id="notes"
                        value={form.notes}
                        onChange={updateField("notes")}
                        rows={3}
                        placeholder="Typical visitor volume, timeline, etc."
                        data-testid="landing-form-notes"
                      />
                    </div>

                    <Button
                      type="submit"
                      disabled={submitting}
                      className="w-full h-12 bg-[#1B4332] hover:bg-[#2D6A4F] text-white text-base font-medium transition-colors"
                      data-testid="landing-form-submit"
                    >
                      {submitting ? (
                        <span className="flex items-center gap-2">
                          <span className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                          Sending request…
                        </span>
                      ) : (
                        <span className="flex items-center gap-2">
                          Send request
                          <ArrowRight className="h-4 w-4" />
                        </span>
                      )}
                    </Button>

                    <p className="text-xs text-[#7A8A84] text-center">
                      By submitting, you agree to be contacted at the email above by the
                      Mission Hospitality team.
                    </p>
                  </form>
                )}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer
        className="border-t border-[#E8E8E6] bg-white"
        data-testid="landing-footer"
      >
        <div className="max-w-6xl mx-auto px-5 md:px-10 py-10 flex flex-col md:flex-row items-start md:items-center justify-between gap-6">
          <div>
            <div className="flex items-center gap-2 mb-2">
              <div className="w-7 h-7 rounded-md bg-[#1B4332] flex items-center justify-center">
                <CheckCircle2 className="h-3.5 w-3.5 text-[#E9F5DB]" />
              </div>
              <span
                className="font-semibold text-[#1B4332]"
                style={{ fontFamily: "Manrope, sans-serif" }}
              >
                Mission Hospitality
              </span>
            </div>
            <p className="text-sm text-[#525C58]">
              Hospitality &amp; housing management for mission hospitals
            </p>
          </div>
          <div className="flex flex-col sm:flex-row gap-4 sm:gap-6 text-sm">
            <a
              href={MAILTO}
              className="text-[#525C58] hover:text-[#1B4332] transition-colors"
              data-testid="landing-footer-contact"
            >
              {CONTACT_EMAIL}
            </a>
            <a
              href="/privacy"
              className="text-[#525C58] hover:text-[#1B4332] transition-colors"
              data-testid="landing-footer-privacy"
            >
              Privacy
            </a>
            <a
              href="/support"
              className="text-[#525C58] hover:text-[#1B4332] transition-colors"
              data-testid="landing-footer-support"
            >
              Support
            </a>
            <Link
              to="/login"
              className="text-[#525C58] hover:text-[#1B4332] transition-colors"
              data-testid="landing-footer-signin"
            >
              Sign in
            </Link>
          </div>
        </div>
      </footer>
    </div>
  );
}
